import React, { useState, useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { setData } from "ducks/actions";
import "./styles.css";
import { createStructuredSelector } from "../../../../node_modules/reselect/es/index";

import {
  createEnterpriseWorkFlow
} from "ducks/actions/index";

import { getRequestId } from "utils/localStorage";

import {
  acraLivenessCheck,
  buildStoredJumioAuthHeaders,
  gotoUrl
} from "utils/helpers";

import { makeSelectCompanyDetails } from "ducks/selectors/planService";

import ErrorModal from "components/EnterpriseAutomated/ErrorModal/index";
import { ENTERPRISE_AUTOMATED } from "utils/constants";

const VERIFICATION_STEPS = [
  "Reviewing your ACRA business profile...",
  "Checking the .oa file...",
  "Verifying your company...",
  "Almost done..."
];

const NETWORK_TIMEOUT = 100000;

const VerificationLoader = ({
  stepDuration = 1000,
  onComplete,
  companyDetails,
  ...props
}) => {
  const dispatch = useDispatch();

  const [currentStep, setCurrentStep] = useState(0);

  const [errorModal, setErrorModal] = useState({
    show: false,
    header: "",
    message: "",
    navigate: ""
  });

  const isMounted = useRef(true);
  const stepTimerRef = useRef(null);
  const networkTimerRef = useRef(null);

  const { uen } = companyDetails || {};

  useEffect(() => {
    if(uen === ""){
      gotoUrl('/enterprise/automated-company-details')
    }
    // eslint-disable-next-line
  },[])

  /**
   * Clear the 10 second network timeout.
   */
  const clearNetworkTimeout = () => {
    if (networkTimerRef.current) {
      clearTimeout(networkTimerRef.current);
      networkTimerRef.current = null;
    }
  };

  /**
   * Close error modal.
   */
  const closeErrorModal = () => {
    if (!isMounted.current) {
      return;
    }

    setErrorModal({
      show: false,
      header: "",
      message: "",
      navigate: ENTERPRISE_AUTOMATED.NAVIGATE_URL
    });
  };

  const showError = error => {
    console.log("SHOWING ERROR MODAL:", error);

    if (!isMounted.current) {
      return;
    }

    const formattedError = {
      header:
        error?.header ||
        "We’re unable to proceed with your order",

      message:
        error?.message ||
        "Your Corporate Plan registration could not be completed following our verification checks."
    };

    setErrorModal({
      show: true,
      header: formattedError.header,
      message: formattedError.message,
      navigate: ENTERPRISE_AUTOMATED.NAVIGATE_URL
    });

    dispatch(
      setData({
        key: "companyVerification",
        data: {
          status: "ERROR",
          error: formattedError
        }
      })
    );
  };

  /**
   * Show network error.
   *
   * This is only called when the API has not returned
   * a response within 10 seconds.
   */
  const showNetworkError = () => {
    console.log(
      "ACRA LIVENESS NETWORK TIMEOUT - NO RESPONSE AFTER 10 SECONDS"
    );

    if (!isMounted.current) {
      return;
    }

    const networkError = {
      header: "Network Error",
      message:
        "We’re unable to verify your company at the moment. Please check your internet connection and try again."
    };

    setErrorModal({
      show: true,
      header: networkError.header,
      message: networkError.message,
      navigate: ENTERPRISE_AUTOMATED.NAVIGATE_URL
    });

    dispatch(
      setData({
        key: "companyVerification",
        data: {
          status: "ERROR",
          error: networkError
        }
      })
    );
  };

  /**
   * Handle successful verification.
   *
   * ONLY response.status === true matters.
   *
   * response.data is not used to determine success.
   */
  const handleSuccess = response => {
    console.log("ACRA VERIFICATION SUCCESS:", response);

    if (!isMounted.current) {
      return;
    }

    clearNetworkTimeout();

    dispatch(
      setData({
        key: "companyVerification",
        data: {
          status: "SUCCESS",
          isVerified: true,
          data: response?.data || null
        }
      })
    );

    if (onComplete) {
      onComplete(response);
    }

    /**
     * Redirect after successful verification.
     */
    gotoUrl("/enterprise/automated-account-details");
  };

  /**
   * Handle failed verification.
   *
   * ONLY response.status === false matters.
   *
   * When status is false, inspect response.error.
   */
  const handleFailure = response => {
    console.log("ACRA VERIFICATION FAILED:", response);
    console.log("ACRA VERIFICATION ERROR:", response?.error);

    if (!isMounted.current) {
      return;
    }

    clearNetworkTimeout();

    const errorDetails = response?.error || {};

    showError({
      header:
        errorDetails.header ||
        "We’re unable to proceed with your order",

      message:
        errorDetails.message ||
        "Your Corporate Plan registration could not be completed following our verification checks."
    });
  };

  const checkVerification = async () => {
    try {
      const headersValue = buildStoredJumioAuthHeaders();
      const request_id = getRequestId();

      const params = {
        request_id,
        uen
      };

      console.log("=================================");
      console.log("ACRA LIVENESS API");
      console.log("Request ID:", request_id);
      console.log("Params:", params);
      console.log("=================================");

      /**
       * Start one 10 second network timeout.
       *
       * This is only for showing the Network Error modal.
       * Axios itself has a 30 second timeout.
       */
      networkTimerRef.current = setTimeout(() => {
        if (!isMounted.current) {
          return;
        }

        console.log(
          "ACRA liveness API did not respond within 10 seconds."
        );

        showNetworkError();
      }, NETWORK_TIMEOUT);

      /**
       * Call ACRA Liveness API once.
       *
       * No polling and no callback.
       */
      const response = await acraLivenessCheck(
        headersValue,
        params
      );

      console.log("=================================");
      console.log("ACRA LIVENESS RESPONSE");
      console.log("Response:", response);
      console.log("=================================");

      if (!isMounted.current) {
        return;
      }

      /**
       * Response received.
       *
       * Clear the 10 second network timeout.
       */
      clearNetworkTimeout();

      /**
       * SUCCESS
       *
       * Only success matters here.
       *
       * We do not inspect:
       * response.data.company_name
       * response.data.uen
       * response.data.company_address
       */
      if (response?.success === true) {
        handleSuccess(response);
        return;
      }

      /**
       * FAILURE
       *
       * Axios function already normalizes the error.
       */
      if (response?.success === false) {
        /**
         * Network error returned by Axios.
         */
        if (
          response?.error?.header === "NETWORK_ERROR"
        ) {
          showNetworkError();
          return;
        }

        /**
         * Verification/business error.
         */
        handleFailure(response);
        return;
      }

      /**
       * Unexpected response.
       *
       * Do not inspect response.data.
       */
      console.log(
        "ACRA response received without a valid success status. No action taken."
      );
    } catch (error) {
      console.error(
        "Error calling ACRA liveness:",
        error
      );

      /**
       * Axios function already handles its own errors
       * and returns { success, error }.
       *
       * This catch is only for unexpected JavaScript errors.
       *
       * Do not immediately show Network Error here because
       * the existing network timeout controls the UI behavior.
       */
    }
  };

  useEffect(() => {
    isMounted.current = true;

    /**
     * Dynamic verification text timer.
     *
     * This is NOT API polling.
     * It only changes the displayed verification text.
     */
    stepTimerRef.current = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < VERIFICATION_STEPS.length - 1) {
          return prev + 1;
        }

        return prev;
      });
    }, stepDuration);

    /**
     * Call verification API only ONCE.
     */
    checkVerification();

    /**
     * Cleanup.
     */
    return () => {
      console.log("VerificationLoader unmounted");

      isMounted.current = false;

      if (stepTimerRef.current) {
        clearInterval(stepTimerRef.current);
        stepTimerRef.current = null;
      }

      clearNetworkTimeout();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stepDuration,
    onComplete,
    acraLivenessCheck
  ]);

  return (
    <div className="verification-loader-container">
      <div className="loader-spinner" />

      <div className="loader-content">
        <h3 className="loader-title">
          {VERIFICATION_STEPS[currentStep]}
        </h3>

        <p className="loader-subtitle">
          This usually takes a few seconds. Please don’t close this page.
        </p>
      </div>

      {errorModal.show && (
        <ErrorModal
          showModal={errorModal.show}
          errorHeader={errorModal.header}
          errorMessage={errorModal.message}
          onClose={closeErrorModal}
          navigate={errorModal.navigate}
        />
      )}
    </div>
  );
};

/**
 * Redux dispatch mapping.
 */
export function mapDispatchToProps(dispatch) {
  return {
    createEnterpriseWorkFlow: (headersValue, params) => {
      return dispatch(
        createEnterpriseWorkFlow(
          headersValue,
          params
        )
      );
    },

    acraLivenessCheck: (
      headersValue,
      params,
      callback
    ) => {
      console.log(
        "DISPATCHING ACRA LIVENESS CHECK"
      );

      return dispatch(
        acraLivenessCheck(
          headersValue,
          params,
          callback
        )
      );
    }
  };
}

const mapStateToProps = createStructuredSelector({
  companyDetails: makeSelectCompanyDetails()
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(VerificationLoader);